import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL
} from 'firebase/storage'
import type Database from '../types/Database'

const ALLOWED_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'application/pdf'
] as const

const MIN_FILE_SIZE = 1024 // 1 KB
const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
const UPLOAD_TIMEOUT_MS = 60_000 // 60 seconds

function validateFile(file: File): void {
  if (
    !ALLOWED_CONTENT_TYPES.includes(
      file.type as (typeof ALLOWED_CONTENT_TYPES)[number]
    )
  ) {
    throw new Error(
      `Invalid file type: ${file.type}. Accepted types: JPEG, PNG, PDF.`
    )
  }

  if (file.size < MIN_FILE_SIZE) {
    throw new Error(`File too small: ${file.size} bytes. Minimum size is 1 KB.`)
  }

  if (file.size > MAX_FILE_SIZE) {
    throw new Error(
      `File too large: ${file.size} bytes. Maximum size is 10 MB.`
    )
  }
}

function uploadToPath(
  file: File,
  storagePath: string,
  onProgress?: (percent: number) => void
): Promise<string> {
  const storage = getStorage()
  const storageRef = ref(storage, storagePath)

  const uploadTask = uploadBytesResumable(storageRef, file, {
    contentType: file.type
  })

  return new Promise<string>((resolve, reject) => {
    const timeout = setTimeout(() => {
      uploadTask.cancel()
      reject(new Error('Upload timed out after 60 seconds.'))
    }, UPLOAD_TIMEOUT_MS)

    uploadTask.on(
      'state_changed',
      snapshot => {
        const percent = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
        onProgress?.(percent)
      },
      error => {
        clearTimeout(timeout)
        if (error.code === 'storage/canceled') {
          return
        }
        reject(error)
      },
      async () => {
        clearTimeout(timeout)
        try {
          const downloadUrl = await getDownloadURL(uploadTask.snapshot.ref)
          resolve(downloadUrl)
        } catch (err) {
          reject(err)
        }
      }
    )
  })
}

export const uploadCertDocument = async (
  file: File,
  userId: string,
  onProgress?: (percent: number) => void
): Promise<string> => {
  validateFile(file)
  return uploadToPath(
    file,
    `accreditations/${userId}/${file.name}`,
    onProgress
  )
}

export const uploadGuideDocument = async (
  file: File,
  userId: string,
  category: Database.GuidedDiveDocCategory,
  onProgress?: (percent: number) => void
): Promise<string> => {
  validateFile(file)
  return uploadToPath(
    file,
    `accreditations/${userId}/guide-docs/${category}/${file.name}`,
    onProgress
  )
}
